/* eslint-disable no-param-reassign */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { showNotification } from 'src/utils/notifications';
import API from '../../../api/api';
import { getTokenData } from 'src/utils/api';
import {
  LOADING_STATUSES,
  TRANSACTIONS_STATUS_APPROVED,
  TRANSACTIONS_STATUS_APPROVED_CR,
} from '../../../constants/statuses';
import {
  isResponseSuccess, formatFilters, getMonthDateRange,
} from '../../../utils/api';
import { INITIAL_FILTERS_STATE } from '../constants/transactionsReport.ts';

// statuses is the real filter; status repeats its first value so an api still running the
// previous release narrows the export to approved rows instead of exporting every status.
const LEDGER_STATUS_FILTER = {
  status: TRANSACTIONS_STATUS_APPROVED.value,
  statuses: [
    TRANSACTIONS_STATUS_APPROVED.value,
    TRANSACTIONS_STATUS_APPROVED_CR.value,
  ].join(','),
};

export const loadTransactionsReport = createAsyncThunk(
  'transactionsReport/loadTransactionsReport',
  async (data, {
    getState,
    rejectWithValue,
  }) => {
    try {
      const { filters, page, per } = getState().transactionsReportReducer;
      const formattedFilters = formatFilters(filters);
      const response = await API(getTokenData()).TRANSACTIONS.REPORT.load({
        ...formattedFilters,
        page: page + 1,
        per,
      });
      if (isResponseSuccess(response.status)) {
        return {
          data: [
            ...response.data,
          ].map((el) => ({ ...el, id: el.month })),
          totalItems: response.total,
        };
      }
      return {};
    } catch (error) {
      return rejectWithValue(error.toString);
    }
  },
);

export const exportTransactionsReport = createAsyncThunk(
  'transactionsReport/exportReport',
  async (month, { rejectWithValue }) => {
    try {
      const { dateFrom, dateTo } = getMonthDateRange(month);

      if (!dateFrom || !dateTo) {
        return rejectWithValue('Unable to determine date range');
      }

      const response = await API(getTokenData()).TRANSACTIONS.REPORT.exportReport({
        date_from: dateFrom,
        date_to: dateTo,
        ...LEDGER_STATUS_FILTER,
      });

      if (isResponseSuccess(response.status)) {
        return {
          success: true,
          message: `Excel report for ${dateFrom} - ${dateTo} has been sent to your email`,
        };
      }
      return rejectWithValue('Failed to request Excel report');
    } catch (error) {
      return rejectWithValue(error.toString());
    }
  },
);

export const exportTransactionsInvoice = createAsyncThunk(
  'transactionsReport/exportInvoice',
  async (month, { rejectWithValue }) => {
    try {
      const { dateFrom, dateTo } = getMonthDateRange(month);

      if (!dateFrom || !dateTo) {
        return rejectWithValue('Unable to determine date range');
      }

      const response = await API(getTokenData()).TRANSACTIONS.REPORT.exportInvoice({
        date_from: dateFrom,
        date_to: dateTo,
        ...LEDGER_STATUS_FILTER,
      });

      if (isResponseSuccess(response.status)) {
        return {
          success: true,
          message: `Invoices archive for ${dateFrom} - ${dateTo} has been sent to your email`,
        };
      }
      return rejectWithValue('Failed to request invoices archive');
    } catch (error) {
      return rejectWithValue(error.toString());
    }
  },
);

const initialState = {
  entity: [],
  page: 0,
  per: 100,
  totalItems: 0,
  filters: INITIAL_FILTERS_STATE,
  status: LOADING_STATUSES.IDLE,
  error: null,
};

const transactionsReportSlice = createSlice({
  name: 'transactionsReport',
  initialState,
  reducers: {
    setPageData: (state, action) => {
      state.page = action.payload.page;
      state.per = action.payload.per;
    },
    setFilters: (state, action) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
      };
    },
    resetTransactionsReport: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadTransactionsReport.pending, (state) => {
        state.status = LOADING_STATUSES.LOADING;
      })
      .addCase(loadTransactionsReport.fulfilled, (state, action) => {
        state.entity = action.payload?.data || [];
        state.totalItems = action.payload?.totalItems || 0;
        state.status = LOADING_STATUSES.SUCCEEDED;
      })
      .addCase(loadTransactionsReport.rejected, (state) => {
        state.status = LOADING_STATUSES.FAILED;
      })
      .addCase(exportTransactionsReport.fulfilled, (state, action) => {
        if (action.payload?.message) {
          showNotification({ type: 'success', text: action.payload.message });
        }
      })
      .addCase(exportTransactionsInvoice.fulfilled, (state, action) => {
        if (action.payload?.message) {
          showNotification({ type: 'success', text: action.payload.message });
        }
      });
  },
});

export const {
  setPageData,
  setFilters,
  resetTransactionsReport,
} = transactionsReportSlice.actions;
export default transactionsReportSlice.reducer;

export const transactionsReportChangePageAction = createAsyncThunk(
  'transactionsReport/transactionsReportChangePageAction',
  async ({
    page = 0,
    per = 0,
  }, {
    dispatch,
    rejectWithValue,
  }) => {
    try {
      await dispatch(setPageData({
        page,
        per,
      }));
      await dispatch(loadTransactionsReport());
      return {};
    } catch (error) {
      return rejectWithValue(error.toString);
    }
  },
);

export const transactionsReportFilterApply = createAsyncThunk(
  'transactionsReport/filterApply',
  async (newFilters, {
    dispatch,
    rejectWithValue,
  }) => {
    try {
      await dispatch(setFilters({
        ...newFilters,
      }));
      await dispatch(loadTransactionsReport());
      return {};
    } catch (error) {
      return rejectWithValue(error.toString);
    }
  },
);
